"use client";

import { motion } from "motion/react";
import { LogoMark } from "../brand/LogoMark";

type AutomationCardProps = {
  label: string;
  title: string;
  description: string;
  features: string[];
  screenshotSrc?: string;
  reverse?: boolean;
};

export function AutomationAgentsSection() {
  return (
    <AutomationSection
      label="Agents"
      title="Build agents that accomplish tasks or entire processes"
      description="Runbook-defined agents help specialists and SMEs automate data extraction, classification, and reasoning as steps or full workflows."
      features={[
        "Extract, classify, and validate data",
        "Use tools to enrich context or take action",
        "Reason over documents, messages, and enterprise data",
        "Generate reports and memos",
      ]}
      screenshotSrc="/assets/screenshots/agent.png"
    />
  );
}

export function AutomationRulesSection() {
  return (
    <AutomationSection
      label="Rules"
      title="Control and govern with deterministic logic"
      description="Visually managed rules let leaders confidently update decision logic without code."
      features={[
        "Codify approval conditions and matrices",
        "Compute risk scores and decision criteria",
        "Validate data and documents",
        "Ensure compliance and governance",
      ]}
      screenshotSrc="/assets/screenshots/rules.png"
      reverse
    />
  );
}

export function AutomationTasksSection() {
  return (
    <AutomationSection
      label="Tasks"
      title="Scale human judgment with context-rich steps"
      description="Approvals and exception-handling interfaces are auto-generated with the right context to move faster."
      features={[
        "Escalate exceptions to managers for review",
        "Request supporting documentation from users",
        "Collaborate on resolving workflow bottlenecks",
      ]}
      screenshotSrc="/assets/screenshots/workflow.png"
    />
  );
}

function AutomationSection({ label, title, description, features, screenshotSrc, reverse }: AutomationCardProps) {
  return (
    <section className="relative flex h-full w-full flex-col justify-center bg-black py-20 lg:py-32">
      <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-8">
        <div className={`flex flex-col gap-12 lg:flex-row lg:items-center lg:gap-24 ${reverse ? "lg:flex-row-reverse" : ""}`}>
          
          {/* Text Content */}
          <motion.div 
            initial={{ opacity: 0, x: reverse ? 20 : -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex-1"
          >
            <div className="mb-6">
              <span className="inline-block rounded-full border border-white px-4 py-1 text-xs font-bold uppercase tracking-widest text-white">
                {label}
              </span>
            </div>
            
            <h3 className="mb-6 text-4xl font-bold leading-tight tracking-tight text-white lg:text-5xl">
              {title}
            </h3>
            
            <p className="mb-8 text-xl leading-relaxed text-white/80">
              {description}
            </p>
            
            <ul className="space-y-4">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-white/90">
                  <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                  <span className="text-lg">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Visual Placeholder */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1"
          >
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-white shadow-2xl">
               {screenshotSrc ? (
                 <img 
                   src={screenshotSrc} 
                   alt={`${label} screenshot`}
                   className="h-full w-full object-cover"
                 />
               ) : (
                 /* Optional: Add subtle details or keep stark white as requested */
                 /* LogoMark in corner for branding */
                 <div className="absolute bottom-6 right-6 text-black">
                     <LogoMark height={32} />
                 </div>
               )}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
